
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Shield, CheckCircle, Eye, FileText, TrendingUp, ExternalLink, Phone } from 'lucide-react';

const VantaPartnership: React.FC = () => {
  return (
    <article className="max-w-4xl mx-auto">
      {/* Introduction */}
      <section className="mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-8 w-8 text-blue-600" />
            <Badge className="bg-green-500 text-white">Partnership Announcement</Badge>
          </div>
          <h2 className="text-2xl font-bold text-blue-800 mb-3">
            Introducing Our Strategic Partnership with Vanta
          </h2>
          <p className="text-gray-700 leading-relaxed">
            We're excited to announce that <strong>IT Management Solutions</strong> has partnered with <a href="https://vanta.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">Vanta</a>, the industry-leading automated cybersecurity and compliance platform. This partnership represents a significant milestone in our mission to provide comprehensive IT security services to small and mid-sized businesses across New York and New Jersey.
          </p>
        </div>
        
        <p className="text-lg text-gray-700 leading-relaxed mb-6">
          As a <strong>cybersecurity company in New York</strong> and <strong>cybersecurity company in New Jersey</strong>, we understand that compliance isn't just about checking boxes—it's about building trust, protecting your business, and ensuring your clients' data remains secure. With Vanta's cutting-edge automation and our hands-on expertise, we're transforming how businesses approach cybersecurity compliance.
        </p>
      </section>

      {/* Why Compliance Matters */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Why Cybersecurity Compliance Is Critical for Small and Mid-Sized Businesses
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800 flex items-center gap-2">
                <Shield className="h-5 w-5" />
                The Compliance Challenge
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-red-700">
                <strong>87% of small businesses</strong> struggle with cybersecurity compliance, leading to failed audits, lost contracts, and regulatory penalties. Without proper compliance frameworks, businesses face:
              </p>
              <ul className="mt-3 space-y-2 text-red-700">
                <li>• Regulatory fines and penalties</li>
                <li>• Loss of client trust and contracts</li>
                <li>• Increased cyber attack vulnerability</li>
                <li>• Costly manual compliance processes</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                The Compliance Advantage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-700">
                Companies with proper <strong>cybersecurity compliance</strong> see significant business benefits:
              </p>
              <ul className="mt-3 space-y-2 text-green-700">
                <li>• 40% faster contract negotiations</li>
                <li>• 60% reduction in security incidents</li>
                <li>• Enhanced client confidence and retention</li>
                <li>• Competitive advantage in regulated markets</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          How Our Vanta Partnership Transforms Your Compliance Journey
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="text-blue-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Faster, Easier Path to Compliance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-3">
                Traditional compliance processes can take 6-12 months. With Vanta's automation and our expertise, we help you achieve <strong>SOC 2 compliance</strong>, <strong>HIPAA compliance</strong>, and <strong>ISO 27001 certification</strong> in weeks, not months.
              </p>
              <ul className="text-gray-600 space-y-1">
                <li>• Automated evidence collection</li>
                <li>• Pre-built compliance frameworks</li>
                <li>• Streamlined audit preparation</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200">
            <CardHeader>
              <CardTitle className="text-purple-800 flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Continuous Security Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-3">
                Never worry about compliance gaps again. Our integrated monitoring provides 24/7 visibility into your security posture, automatically detecting risks and ensuring continuous compliance.
              </p>
              <ul className="text-gray-600 space-y-1">
                <li>• Real-time risk detection</li>
                <li>• Automated remediation guidance</li>
                <li>• Proactive threat identification</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-green-200">
            <CardHeader>
              <CardTitle className="text-green-800 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Clear Audit Trails and Reporting
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-3">
                Comprehensive, auditor-ready documentation is generated automatically. Our <strong>IT compliance solutions</strong> provide clear evidence trails that satisfy the most stringent audit requirements.
              </p>
              <ul className="text-gray-600 space-y-1">
                <li>• Automated compliance reporting</li>
                <li>• Audit-ready documentation</li>
                <li>• Historical compliance tracking</li>
              </ul>
            </CardContent>
          </Card>
          
          <Card className="border-orange-200">
            <CardHeader>
              <CardTitle className="text-orange-800 flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Better Trust and Credibility
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-3">
                Demonstrate your commitment to <strong>data security for small business</strong> with recognized compliance certifications. Win more contracts and build stronger client relationships.
              </p>
              <ul className="text-gray-600 space-y-1">
                <li>• Increased client confidence</li>
                <li>• Competitive differentiation</li>
                <li>• Market credibility boost</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Industry-Specific Solutions */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Specialized Compliance Solutions for Regulated Industries
        </h2>
        
        <div className="space-y-6">
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="text-blue-800">Healthcare Providers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-blue-700 mb-3">
                <strong>HIPAA compliance</strong> is non-negotiable for healthcare organizations. Our partnership with Vanta ensures your practice meets all HIPAA requirements while maintaining operational efficiency.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-blue-800 mb-2">Key Benefits:</h4>
                  <ul className="text-blue-700 space-y-1">
                    <li>• Automated HIPAA risk assessments</li>
                    <li>• PHI protection monitoring</li>
                    <li>• Breach notification compliance</li>
                    <li>• Staff training documentation</li>
                  </ul>
                </div>
                <div>
                  <p className="text-blue-700">
                    <Link to="/services/specialty-services" className="text-blue-600 hover:text-blue-800 underline">
                      Learn more about our healthcare IT services
                    </Link>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800">Real Estate Agencies</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-green-700 mb-3">
                Real estate firms handle sensitive financial and personal data. Our <strong>IT security services</strong> help you protect client information while maintaining compliance with industry regulations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-green-800 mb-2">Key Benefits:</h4>
                  <ul className="text-green-700 space-y-1">
                    <li>• Client data protection</li>
                    <li>• Transaction security monitoring</li>
                    <li>• Document management compliance</li>
                    <li>• Multi-location security coordination</li>
                  </ul>
                </div>
                <div>
                  <p className="text-green-700">
                    <Link to="/blog/technology-solutions-real-estate-agencies" className="text-green-600 hover:text-green-800 underline">
                      Read our real estate technology guide
                    </Link>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="text-purple-800">Financial Services</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-purple-700 mb-3">
                Financial firms require the highest levels of security and compliance. Our <strong>SOC 2 compliance</strong> solutions ensure your business meets stringent financial industry standards.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-purple-800 mb-2">Key Benefits:</h4>
                  <ul className="text-purple-700 space-y-1">
                    <li>• SOC 2 Type II certification</li>
                    <li>• Financial data encryption</li>
                    <li>• Access control management</li>
                    <li>• Regulatory reporting automation</li>
                  </ul>
                </div>
                <div>
                  <p className="text-purple-700">
                    <Link to="/blog/secure-document-management-cpa-firms" className="text-purple-600 hover:text-purple-800 underline">
                      Discover our CPA security solutions
                    </Link>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Regional Coverage */}
      <section className="mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">
            Comprehensive Coverage Across New York and New Jersey
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold mb-3">Our <strong>Managed IT Services NY NJ</strong> Include:</h3>
              <ul className="space-y-2">
                <li>• On-site compliance assessments</li>
                <li>• Remote security monitoring</li>
                <li>• Emergency response support</li>
                <li>• Staff training and education</li>
                <li>• Ongoing compliance maintenance</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-3">Serving Key Markets:</h3>
              <ul className="space-y-2">
                <li>• Manhattan and NYC metro area</li>
                <li>• Long Island and Westchester County</li>
                <li>• Northern and Central New Jersey</li>
                <li>• Remote support throughout NY/NJ</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Your Path to Compliance: How It Works
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="text-center">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold">1</span>
              </div>
              <CardTitle className="text-lg">Assessment</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Comprehensive security and compliance gap analysis
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardHeader>
              <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold">2</span>
              </div>
              <CardTitle className="text-lg">Integration</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Seamless Vanta platform deployment and configuration
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold">3</span>
              </div>
              <CardTitle className="text-lg">Implementation</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Automated compliance monitoring and reporting setup
              </p>
            </CardContent>
          </Card>
          
          <Card className="text-center">
            <CardHeader>
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="font-bold">4</span>
              </div>
              <CardTitle className="text-lg">Certification</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600">
                Audit preparation and compliance certification achievement
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Strategic Partnership Value */}
      <section className="mb-8">
        <div className="bg-gray-50 p-6 rounded-lg border">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            More Than an IT Provider: Your Strategic Cybersecurity Partner
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            As your <strong>Vanta compliance partner</strong>, we go beyond traditional IT support. We become an extension of your team, providing strategic guidance, proactive monitoring, and ongoing support to ensure your business stays secure and compliant.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Proactive Protection</h3>
              <p className="text-sm text-gray-600">Identify and address risks before they become problems</p>
            </div>
            <div className="text-center">
              <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Business Growth</h3>
              <p className="text-sm text-gray-600">Compliance as a competitive advantage and growth enabler</p>
            </div>
            <div className="text-center">
              <Phone className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">Ongoing Support</h3>
              <p className="text-sm text-gray-600">Dedicated support team available when you need us</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="mb-8">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Streamline Your Cybersecurity Compliance?
          </h2>
          <p className="text-xl mb-6">
            Don't let compliance challenges hold your business back. Our expert team is ready to help you achieve faster, more reliable compliance with Vanta's industry-leading automation.
          </p>
          
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 border border-white">
              <Link to="/book">
                <Phone className="h-5 w-5 mr-2" />
                Book Your Free Consultation
              </Link>
            </Button>
            
            <Button asChild size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600">
              <a href="tel:+1-877-748-6468">
                Call 877-7IT-MGMT
              </a>
            </Button>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-blue-100 mb-2">
              <strong>Serving businesses across New York and New Jersey</strong>
            </p>
            <p className="text-sm text-blue-200">
              On-site and remote support • Emergency response available • No long-term contracts required
            </p>
          </div>
        </div>
      </section>

      {/* Additional Resources */}
      <section className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          Additional Resources
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5" />
                Learn More About Vanta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                Discover how Vanta's automated compliance platform is transforming cybersecurity for businesses worldwide.
              </p>
              <Button asChild variant="outline">
                <a href="https://vanta.com" target="_blank" rel="noopener noreferrer">
                  Visit Vanta's Website
                  <ExternalLink className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Related Articles</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                <li>
                  <Link to="/blog/cybersecurity-best-practices-small-business" className="text-blue-600 hover:text-blue-800 underline">
                    5 Cybersecurity Best Practices Every Small Business Should Implement
                  </Link>
                </li>
                <li>
                  <Link to="/blog/zero-trust-security-small-businesses" className="text-blue-600 hover:text-blue-800 underline">
                    Why Zero-Trust Security is Non-Negotiable for Small Businesses
                  </Link>
                </li>
                <li>
                  <Link to="/services/cybersecurity" className="text-blue-600 hover:text-blue-800 underline">
                    Our Cybersecurity Services
                  </Link>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>
    </article>
  );
};

export default VantaPartnership;
